import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { NXDProvider } from '@n26/nxd'
import { router } from './router'
import { AuthProvider } from '@/features/auth/model/AuthContext'
import { useSystemTheme } from '@/shared/hooks/useSystemTheme'
import { useHtmlLang } from '@/shared/hooks/useHtmlLang'
import { useLocale } from '@/shared/hooks/useLocale'
import './i18n'
import './styles/fonts.css'
import '@n26/nxd/styles.css'
import './styles/globals.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
})

const Root = () => {
  useSystemTheme()

  const { locale } = useLocale()

  useHtmlLang(locale)

  return (
    <QueryClientProvider client={queryClient}>
      <NXDProvider locale={locale}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </NXDProvider>
    </QueryClientProvider>
  )
}

const bootstrap = async (): Promise<void> => {
  if (import.meta.env.VITE_USE_MSW === 'true') {
    const { worker } = await import('./mocks/browser')
    await worker.start({ onUnhandledRequest: 'bypass' })
  }

  const container = document.getElementById('root')
  if (!container) throw new Error('#root not found')
  createRoot(container).render(
    <StrictMode>
      <Root />
    </StrictMode>
  )
}

void bootstrap()
